import * as XLSX from 'xlsx';

export const generateInvoiceExcel = (invoiceData, companyData = null) => {
  try {
    // Format date helper
    const formatDate = (dateString) => {
      if (!dateString) return "";
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    };

    // Create workbook
    const wb = XLSX.utils.book_new();
    
    // Prepare data for Excel
    const excelData = [];
    
    // Company Header
    excelData.push(['', '', '', 'INVOICE']); // Centered INVOICE
    excelData.push(['Faman Services & Solutions®']);
    excelData.push(['43/1582, Ground Floor, Kochapilly Road, Palarivattom']);
    excelData.push(['Ernakulam, Kerala – 682025, India']);
    excelData.push(['Email: support@faman.in | Website: www.faman.in | Ph No.: 9633940163']);
    excelData.push(['GSTIN: 32AAFFF1487P1Z3 | STATE: KERALA | STATE CODE: 32']);
    excelData.push([]);
    
    // Customer Info (Left) and Invoice Details (Right) - Proper Layout
    // Row 1: BILLED TO label in col A | Invoice Number label + value (right)
    excelData.push(['BILLED TO:', '', '', '', '', 'Invoice Number', invoiceData.InvoicePrint?.InvoiceNo || '']);
    
    // Row 2: Customer Name in col B (next to BILLED TO) | Invoice Date label + value (right)
    excelData.push(['', invoiceData.InvoicePrint?.CustomerName || '', '', '', '', 'Invoice Date', formatDate(invoiceData.InvoicePrint?.InvoiceDate)]);
    
    // Row 3: Address1 in col B | QA Number label + value (right)
    excelData.push(['', invoiceData.InvoicePrint?.CustomerAddress1 || '', '', '', '', 'QA Number', invoiceData.InvoicePrint?.QANo || '']);
    
    // Row 4: Address2 in col B | Site label + value (right)
    if (invoiceData.InvoicePrint?.CustomerAddress2) {
      excelData.push(['', invoiceData.InvoicePrint.CustomerAddress2, '', '', '', 'Site', invoiceData.InvoicePrint?.CustomerLocation || '']);
    }
    
    // Row 5: Location in col B
    excelData.push(['', invoiceData.InvoicePrint?.CustomerLocation || '']);
    
    // GST Number row - Label in col A, value in col B
    if (invoiceData.InvoicePrint?.CustomerGSTNumber) {
      excelData.push(['GST No:', invoiceData.InvoicePrint.CustomerGSTNumber]);
    }
    
    excelData.push([]);
    
    // Get items from InvoicePrintList
    const tableData = invoiceData.InvoicePrintList || [];
    
    // Check if IGST is used
    const isIGST = tableData.some(item => (item.IGSTAmount ?? 0) > 0);
    
    // Table Headers
    if (isIGST) {
      excelData.push(['Sl.No', 'Description', 'HSN', 'UOM', 'Qty', 'Unit Price', 'Taxable', 'GST %', 'IGST', 'Total']);
    } else {
      excelData.push(['Sl.No', 'Description', 'HSN', 'UOM', 'Qty', 'Unit Price', 'Taxable', 'GST %', 'CGST', 'SGST', 'Total']);
    }
    
    // Add items to Excel
    tableData.forEach((item, index) => {
      // Build description: WorkItemName / WorkDetailsName (ItemRemark)
      let description = "";
      
      if (item.WorkItemName) {
        description = item.WorkItemName;
      }
      
      if (item.WorkDetailsName || item.Description) {
        description += (description ? " / " : "") + (item.WorkDetailsName || item.Description);
      }
      
      if (item.ItemRemark && item.ItemRemark.trim() !== "") {
        description += ` (${item.ItemRemark})`;
      }
      
      if (isIGST) {
        excelData.push([
          index + 1,
          description,
          item.HSCODE || '',
          item.InvoiceUOM || item.UOM || '',
          1,
          parseFloat(item.TaxableAmount || 0).toFixed(2),
          parseFloat(item.TaxableAmount || 0).toFixed(2),
          `${item.GSTRate || 0}%`,
          parseFloat(item.IGSTAmount || 0).toFixed(2),
          parseFloat(item.TotalAmount || 0).toFixed(2)
        ]);
      } else {
        excelData.push([
          index + 1,
          description,
          item.HSCODE || '',
          item.InvoiceUOM || item.UOM || '',
          1,
          parseFloat(item.TaxableAmount || 0).toFixed(2),
          parseFloat(item.TaxableAmount || 0).toFixed(2),
          `${item.GSTRate || 0}%`,
          parseFloat(item.CGSTAmount || 0).toFixed(2),
          parseFloat(item.SGSTAmount || 0).toFixed(2),
          parseFloat(item.TotalAmount || 0).toFixed(2)
        ]);
      }
    });
    
    // Calculate totals
    const totalTaxable = tableData.reduce((sum, item) => sum + (item.TaxableAmount ?? 0), 0);
    const totalCGST = tableData.reduce((sum, item) => sum + (item.CGSTAmount ?? 0), 0);
    const totalSGST = tableData.reduce((sum, item) => sum + (item.SGSTAmount ?? 0), 0);
    const totalIGST = tableData.reduce((sum, item) => sum + (item.IGSTAmount ?? 0), 0);
    const totalAmount = tableData.reduce((sum, item) => sum + (item.TotalAmount ?? 0), 0);
    
    // Add totals row
    if (isIGST) {
      excelData.push([
        '',
        'Total:',
        '',
        '',
        '',
        '',
        totalTaxable.toFixed(2),
        '',
        totalIGST.toFixed(2),
        totalAmount.toFixed(2)
      ]);
    } else {
      excelData.push([
        '',
        'Total:',
        '',
        '',
        '',
        '',
        totalTaxable.toFixed(2),
        '',
        totalCGST.toFixed(2),
        totalSGST.toFixed(2),
        totalAmount.toFixed(2)
      ]);
    }
    
    // Add summary section
    excelData.push([]);
    excelData.push(['', '', '', '', '', 'Taxable Amount:', totalTaxable.toFixed(2)]);
    if (isIGST) {
      excelData.push(['', '', '', '', '', 'IGST Amount:', totalIGST.toFixed(2)]);
    } else {
      excelData.push(['', '', '', '', '', 'CGST Amount:', totalCGST.toFixed(2)]);
      excelData.push(['', '', '', '', '', 'SGST Amount:', totalSGST.toFixed(2)]);
    }
    excelData.push(['', '', '', '', '', 'Round Off:', parseFloat(invoiceData.InvoicePrint?.RoundOff || 0).toFixed(2)]);
    excelData.push(['', '', '', '', '', 'Grand Total:', parseFloat(invoiceData.InvoicePrint?.TotalAmount || totalAmount).toFixed(2)]);
    
    // Terms & Conditions
    if (invoiceData.InvoiceTermsAndCondition && invoiceData.InvoiceTermsAndCondition.length > 0) {
      excelData.push([]);
      excelData.push(['Terms & Conditions:']);
      invoiceData.InvoiceTermsAndCondition.forEach((term, index) => {
        excelData.push([`${index + 1}. ${term.Name || ''}`]);
      });
    }
    
    // ============================================================
    // 🔹 ANNEXURE SECTION - InvoicePrintItems (Detailed Items) - SAME SHEET
    // ============================================================
    
    // Check if InvoicePrintItems exists and has data
    const hasInvoicePrintItems = invoiceData.InvoicePrintItems && 
                            Array.isArray(invoiceData.InvoicePrintItems) && 
                            invoiceData.InvoicePrintItems.length > 0;
    
    if (hasInvoicePrintItems) {
      // Add spacing before annexure
      excelData.push([]);
      excelData.push([]);
      
      // Annexure Header
      excelData.push(['ANNEXURE - DETAILED ITEMS']);
      excelData.push([]);
      
      // Group by WorkName
      const groupedSections = invoiceData.InvoicePrintItems.reduce((acc, row) => {
        const groupKey = row.WorkName || row.InvoiceChildDetailName || "Uncategorized";
        if (!acc[groupKey]) acc[groupKey] = [];
        acc[groupKey].push(row);
        return acc;
      }, {});
      
      let continuousSlNo = 1;
      let grandTotal = 0;
      
      // Iterate over grouped sections
      Object.entries(groupedSections).forEach(([sectionName, rows]) => {
        // Section Header
        excelData.push([sectionName]);
        excelData.push(['Sl.No', 'Category', 'UOM', 'QTY', 'Rate', 'Total']);
        
        let sectionSubtotal = 0;
        
        // Add items
        rows.forEach((row) => {
          // Build description
          let description = "";
          
          if (row.WorkItemName) {
            description = row.WorkItemName;
          }
          
          if (row.WorkDetailsName) {
            description += (description ? " / " : "") + row.WorkDetailsName;
          }
          
          if (row.ItemRemark && row.ItemRemark.trim() !== "") {
            description += ` (${row.ItemRemark})`;
          }
          
          const itemTotal = parseFloat(row.TaxableAmount || 0);
          sectionSubtotal += itemTotal;
          
          excelData.push([
            continuousSlNo++,
            description,
            row.UOM || '',
            row.Quantity || 0,
            parseFloat(row.Rate || 0).toFixed(2),
            itemTotal.toFixed(2)
          ]);
        });
        
        // Section subtotal
        excelData.push(['', '', '', '', 'Subtotal:', sectionSubtotal.toFixed(2)]);
        excelData.push([]);
        
        grandTotal += sectionSubtotal;
      });
      
      // Grand Total for Annexure
      excelData.push(['', '', '', '', 'Grand Total:', grandTotal.toFixed(2)]);
    }
    
    // Create worksheet
    const ws = XLSX.utils.aoa_to_sheet(excelData);
    
    // Set column widths
    ws['!cols'] = [
      { wch: 8 },   // Sl.No
      { wch: 50 },  // Description
      { wch: 12 },  // HSN
      { wch: 10 },  // UOM
      { wch: 8 },   // Qty
      { wch: 12 },  // Unit Price
      { wch: 12 },  // Taxable
      { wch: 8 },   // GST %
      { wch: 12 },  // CGST/IGST
      { wch: 12 },  // SGST (if not IGST)
      { wch: 15 }   // Total
    ];
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Invoice');
    
    // Generate filename with invoice number and date
    const invoiceNo = invoiceData.InvoicePrint?.InvoiceNo || 'Invoice';
    const date = new Date().toISOString().split('T')[0];
    const filename = `${invoiceNo}_${date}.xlsx`;
    
    // Download file
    XLSX.writeFile(wb, filename);
    
    console.log('Invoice Excel generated successfully');
  } catch (error) {
    console.error('Error generating Invoice Excel:', error);
    alert('Failed to generate Excel file. Please try again.');
  }
};
